import { Badge, Button, Surface } from '@chopchop-se/ui';
import type { ProductionRequestListItem, ProductionRequestStatus } from '@warehouse/shared';
import { CalendarClock, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PRODUCTION_REQUEST_TONES, REQUEST_COLUMNS } from '../lib/status';
import { formatDateTime, formatQty } from '../../../lib/format';

/**
 * Production → warehouse goods requests as a kanban (spec §5.9).
 *
 * The lanes ARE the workflow: requested → accepted (a move task now exists) →
 * delivered (the stock actually moved), with rejected as the dead end. Actions
 * live on the card because that is where the reader's eye already is, and each
 * one is a single command — no multi-step wizard for a one-line request.
 */
export interface RequestKanbanProps {
  requests: readonly ProductionRequestListItem[];
  canManage: boolean;
  busyRequestId?: string | null;
  onAccept: (request: ProductionRequestListItem) => void;
  onReject: (request: ProductionRequestListItem) => void;
  onDeliver: (request: ProductionRequestListItem) => void;
  emptyLabel: string;
}

export function RequestKanban({
  requests,
  canManage,
  busyRequestId,
  onAccept,
  onReject,
  onDeliver,
  emptyLabel,
}: RequestKanbanProps): ReactNode {
  const { t } = useTranslation('production');

  const byStatus = new Map<ProductionRequestStatus, ProductionRequestListItem[]>(
    REQUEST_COLUMNS.map((status) => [status, []]),
  );
  for (const request of requests) byStatus.get(request.status)?.push(request);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {REQUEST_COLUMNS.map((status) => {
        const column = byStatus.get(status) ?? [];
        return (
          <section key={status} aria-label={t(`requests.columns.${status}`)} className="flex flex-col gap-3">
            <header className="flex items-center justify-between px-1">
              <h2 className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-ink-muted">
                {t(`requests.columns.${status}`)}
              </h2>
              <span className="text-xs text-ink-muted">{column.length}</span>
            </header>

            {column.length === 0 ? (
              <Surface className="p-4 text-sm text-ink-muted">{emptyLabel}</Surface>
            ) : (
              column.map((request) => (
                <Surface key={request.id} className="flex flex-col gap-2.5 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium text-ink">{request.articleName}</h3>
                      <p className="truncate font-mono text-xs text-ink-muted">{request.articleNo}</p>
                    </div>
                    <Badge tone={PRODUCTION_REQUEST_TONES[request.status]} size="sm" dot>
                      {t(`requests.status.${request.status}`)}
                    </Badge>
                  </div>

                  <p className="text-sm text-ink">
                    {formatQty(request.qtyBase, { unit: request.unitCode ?? undefined })}
                  </p>

                  {request.note ? <p className="text-sm text-ink-muted">{request.note}</p> : null}

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
                    <span className="inline-flex items-center gap-1">
                      <User className="size-3.5" aria-hidden />
                      {request.requestedByName ?? '—'}
                    </span>
                    {request.neededAt ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="size-3.5" aria-hidden />
                        {formatDateTime(request.neededAt)}
                      </span>
                    ) : null}
                  </div>

                  {canManage ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {request.status === 'requested' ? (
                        <>
                          <Button
                            size="sm"
                            loading={busyRequestId === request.id}
                            onClick={() => {
                              onAccept(request);
                            }}
                          >
                            {t('requests.actions.accept')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              onReject(request);
                            }}
                          >
                            {t('requests.actions.reject')}
                          </Button>
                        </>
                      ) : null}
                      {request.status === 'accepted' ? (
                        <Button
                          size="sm"
                          loading={busyRequestId === request.id}
                          onClick={() => {
                            onDeliver(request);
                          }}
                        >
                          {t('requests.actions.deliver')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </Surface>
              ))
            )}
          </section>
        );
      })}
    </div>
  );
}
